import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import QRCode from 'qrcode';

interface QRPairingState {
  pairingCode: string | null;
  qrCodeUrl: string | null;
  isWaiting: boolean;
  error: string | null;
}

interface PairingSession {
  id: string;
  code: string;
  deviceType: 'tv' | 'mobile' | 'desktop' | 'tablet';
  status: 'pending' | 'connected' | 'expired';
  createdAt: Date;
}

/**
 * Hook for TV QR code pairing and cross-device sync
 */
export function useQRPairing(deviceType: 'tv' | 'mobile' | 'desktop' | 'tablet' = 'desktop') {
  const [state, setState] = useState<QRPairingState>({
    pairingCode: null,
    qrCodeUrl: null,
    isWaiting: false,
    error: null,
  });
  const [isPaired, setIsPaired] = useState(false);
  const [pairedDevices, setPairedDevices] = useState<string[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const generatePairingCode = async () => {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    const session: PairingSession = {
      id: crypto.randomUUID(),
      code,
      deviceType,
      status: 'pending',
      createdAt: new Date(),
    };

    setState({
      pairingCode: code,
      qrCodeUrl: null,
      isWaiting: true,
      error: null,
    });
    setSessionId(session.id);

    // Generate QR code
    const pairingUrl = `${window.location.origin}/pair?code=${code}`;
    const qrUrl = await QRCode.toDataURL(pairingUrl, {
      width: 300,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#ffffff',
      },
    });
    
    setState(prev => ({ ...prev, qrCodeUrl: qrUrl }));

    // Store session
    localStorage.setItem(`pairing_${session.id}`, JSON.stringify(session));

    // Auto-expire after 5 minutes
    setTimeout(() => {
      clearPairingSession();
    }, 5 * 60 * 1000);

    return code;
  };

  const joinSession = async (code: string): Promise<boolean> => {
    const allKeys = Object.keys(localStorage);
    const pairingKeys = allKeys.filter(k => k.startsWith('pairing_'));
    
    for (const key of pairingKeys) {
      const sessionData = localStorage.getItem(key);
      if (sessionData) {
        const session: PairingSession = JSON.parse(sessionData);
        if (session.code === code && session.status === 'pending') {
          session.status = 'connected';
          localStorage.setItem(key, JSON.stringify(session));
          
          const pairedList = JSON.parse(localStorage.getItem('paired_devices') || '[]');
          pairedList.push(session.id);
          localStorage.setItem('paired_devices', JSON.stringify(pairedList));
          
          setIsPaired(true);
          setSessionId(session.id);
          return true;
        }
      }
    }
    
    return false;
  };

  const clearPairingSession = () => {
    if (sessionId) {
      localStorage.removeItem(`pairing_${sessionId}`);
    }
    setState({
      pairingCode: null,
      qrCodeUrl: null,
      isWaiting: false,
      error: null,
    });
    setSessionId(null);
  };

  const disconnect = () => {
    setIsPaired(false);
    setPairedDevices([]);
    clearPairingSession();
    localStorage.removeItem('paired_devices');
  };

  const startPairing = () => {
    generatePairingCode();
    
    return () => {
      clearPairingSession();
    };
  };

  const cancelPairing = () => {
    clearPairingSession();
  };

  useEffect(() => {
    const pairedList = JSON.parse(localStorage.getItem('paired_devices') || '[]');
    if (pairedList.length > 0) {
      setIsPaired(true);
      setPairedDevices(pairedList);
    }
  }, []);

  return {
    ...state,
    isPaired,
    pairedDevices,
    sessionId,
    startPairing,
    cancelPairing,
    generatePairingCode,
    joinSession,
    disconnect,
    clearPairingSession,
  };
}
